import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider, KeyboardDatePicker, KeyboardDateTimePicker } from '@material-ui/pickers';
import ChevronSm from '@alltrails/shared/icons/ChevronSm';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import DateFnsUtils from '@date-io/date-fns';
import {
  COLOR_BACKGROUND_SECONDARY,
  COLOR_INTERACTIVE_DEFAULT,
  COLOR_INTERACTIVE_PRIMARY,
  COLOR_TEXT_LINK_PRIMARY,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  FONT_FAMILY_PRIMARY,
  FONT_SIZE_100,
  SPACE_12,
  SPACE_16
} from '@alltrails/shared/denali/tokens';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import * as styles from './DatePicker.module.scss';
import { localeMap, localizedFormat } from '../../../utils/time_helpers';

const TextFieldComponent = (props: TextFieldProps) => <TextField {...props} disabled />;

const fontFamily = `${FONT_FAMILY_PRIMARY}, Arial, sans-serif`;

const palette = {
  primary: {
    light: COLOR_INTERACTIVE_DEFAULT,
    main: COLOR_INTERACTIVE_PRIMARY,
    dark: COLOR_INTERACTIVE_DEFAULT
  }
};

const typography = {
  body1: {
    fontFamily,
    color: COLOR_TEXT_PRIMARY
  },
  body2: {
    fontFamily,
    fontSize: FONT_SIZE_100,
    color: COLOR_TEXT_PRIMARY
  },
  caption: {
    fontFamily,
    fontSize: FONT_SIZE_100,
    color: COLOR_TEXT_SECONDARY
  }
};

// if we want the picker to be full width, dynamically
// create the dimensions for the calendar
const getFullWidthSizing = (fullWidth: boolean, pickerWidth: number) => {
  let paperProps = {};
  let overrides: any = {
    MuiPickersBasePicker: {
      container: {
        paddingBottom: `${SPACE_16}px`
      }
    },
    MuiTouchRipple: {
      ripple: {
        display: 'none'
      }
    }
  };

  if (!fullWidth || !pickerWidth) {
    return { paperProps, overrides };
  }

  paperProps = {
    style: { width: pickerWidth, height: pickerWidth * 0.84 }
  };

  const dayWidth = Math.min(pickerWidth * 0.12, 50);

  overrides = {
    MuiPickersBasePicker: {
      container: {
        paddingTop: `${SPACE_12}px`
      },
      pickerView: {
        minWidth: '100%',
        'overflow-x': 'visible'
      }
    },
    MuiPickersCalendarHeader: {
      daysHeader: {
        marginBottom: `${SPACE_16}px`,
        justifyContent: 'space-evenly'
      },
      dayLabel: {
        width: dayWidth
      },
      iconButton: {
        height: dayWidth,
        width: dayWidth
      }
    },
    MuiPickersCalendar: {
      week: {
        justifyContent: 'space-evenly'
      }
    },
    MuiPickersDay: {
      day: {
        width: dayWidth,
        height: dayWidth
      },
      current: {
        background: COLOR_BACKGROUND_SECONDARY,
        color: COLOR_INTERACTIVE_PRIMARY
      }
    },
    MuiTouchRipple: {
      ripple: {
        display: 'none'
      }
    }
  };

  return { paperProps, overrides };
};

type DatePickerProps = {
  abbreviateMonth?: boolean;
  date: Date | string;
  disableFuture?: boolean;
  disablePast?: boolean;
  fullWidth?: boolean;
  materialRootClass?: string;
  maxDate?: Date | string;
  minDate?: Date | string;
  onChange?: (date: Date) => void;
  showTime?: boolean;
  slim?: boolean;
  pickerId?: string;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=586%3A31919
const DatePicker = ({
  abbreviateMonth = false,
  date,
  disableFuture = true,
  disablePast = false,
  fullWidth = false,
  materialRootClass,
  maxDate,
  minDate,
  onChange,
  showTime = false,
  slim,
  pickerId
}: DatePickerProps) => {
  const languageRegionCode = useLanguageRegionCode();
  const [isOpen, toggleOpen] = useState(false);
  const [pickerWidth, updatePickerWidth] = useState(null);
  const KeyboardIcon = <ChevronSm orientation={isOpen ? 'up' : 'down'} color={isOpen && COLOR_TEXT_LINK_PRIMARY} />;
  const rootStyles = isOpen ? styles.expandedRoot : styles.root;
  const pickId = pickerId ? pickerId : "date-picker-inline"
  const containerId = pickerId ? `keyboardDatePicker-${pickerId}` : "keyboardDatePicker"

  useEffect(() => {
    if (fullWidth) {
      updatePickerWidth(document.getElementById('keyboardDatePicker').clientWidth);
    }
  }, [isOpen]);

  const { paperProps, overrides } = getFullWidthSizing(fullWidth, pickerWidth);

  const theme = createMuiTheme({
    palette,
    typography,
    overrides
  });

  const PickerComponent = showTime ? KeyboardDateTimePicker : KeyboardDatePicker;

  return (
    <div id={containerId}>
      <ThemeProvider theme={theme}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[languageRegionCode]}>
          <PickerComponent
            disableToolbar
            variant="inline"
            onClick={() => toggleOpen(true)}
            onClose={() => toggleOpen(false)}
            open={isOpen}
            className={classNames(rootStyles, materialRootClass, { [styles.slim]: slim })}
            disableFuture={disableFuture}
            disablePast={disablePast}
            minDate={minDate}
            maxDate={maxDate}
            format={localizedFormat(abbreviateMonth, languageRegionCode, showTime)}
            margin="normal"
            id={pickId}
            value={date}
            onChange={onChange}
            KeyboardButtonProps={{
              'aria-label': 'change date',
              disableRipple: true,
              disableFocusRipple: true,
              disableTouchRipple: true
            }}
            InputProps={{
              disableUnderline: true,
              classes: { root: styles.textInput }
            }}
            PopoverProps={{
              classes: { paper: styles.popoverRoot },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: -16,
                horizontal: 14
              },
              getContentAnchorEl: null,
              PaperProps: paperProps
            }}
            keyboardIcon={KeyboardIcon}
            TextFieldComponent={TextFieldComponent}
          />
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    </div>
  );
};

export default DatePicker;
